import { apiService, publicApiService, validateAndAssert } from '@app/services/api-service';
import { EmailPreferenceInfoOutSchema } from './email.schema';
import type { EmailPreferenceInDtoSchema, EmailPreferenceUpdateInDtoSchema } from './email.schema';
import {
  AdminActionInDto,
  CustomerEmailOverviewOutDto,
  CustomerRenderedMailOutDto,
  CustomerSearchResultOutDto,
  EEmailReportGranularity,
  EmailDeliverabilityOutDto,
  EmailReportSeriesOutDto,
  EmailReportSummaryOutDto,
  ETimelineRecordType,
  PageResponseEmailLogEntryOutDto,
} from '@app/swagger-types';
import { APIPaginationQueryParams } from '@app/models/api';

export abstract class EmailApi {
  static async getEmailPreferences(token: string) {
    return validateAndAssert(
      publicApiService.get(`/public/email-preferences?token=${token}`),
      EmailPreferenceInfoOutSchema
    );
  }

  static async saveEmailPreferences(body: EmailPreferenceInDtoSchema) {
    return validateAndAssert(publicApiService.put(`/public/email-preferences`, body), EmailPreferenceInfoOutSchema);
  }

  static async unsubscribeEmailViaToken(token: string) {
    return publicApiService.post(`/public/unsubscribe-email?token=${token}`);
  }

  // Authenticated-session siblings of the two above (account settings, logged in - no token
  // needed) - same email_preference model, ProfileController.me/email-preferences.
  static async getMyEmailPreferences() {
    return validateAndAssert(apiService.get(`/private/auth/me/email-preferences`), EmailPreferenceInfoOutSchema);
  }

  static async saveMyEmailPreferences(body: EmailPreferenceUpdateInDtoSchema) {
    return validateAndAssert(apiService.put(`/private/auth/me/email-preferences`, body), EmailPreferenceInfoOutSchema);
  }

  // admin-emailbeheer blok A stap 5, Scherm 2 - Per klant.

  static async adminGetCustomerEmailOverview(userId: string) {
    const { data } = await apiService.get<CustomerEmailOverviewOutDto>(`/private/admin/email-customers/${userId}`);
    return data;
  }

  static async adminSearchCustomers(term: string) {
    const { data } = await apiService.get<CustomerSearchResultOutDto[]>(`/private/admin/email-customers/search`, {
      params: { term },
    });
    return data;
  }

  static async adminCancelScheduledEmail(userId: string, scheduleId: string, body: AdminActionInDto) {
    await apiService.post(`/private/admin/email-customers/${userId}/schedules/${scheduleId}/cancel`, body);
  }

  static async adminUnblockCustomerAddress(userId: string, body: AdminActionInDto) {
    await apiService.post(`/private/admin/email-customers/${userId}/unblock`, body);
  }

  static async adminViewRenderedEmail(userId: string, recordType: ETimelineRecordType, recordId: string) {
    const { data } = await apiService.get<CustomerRenderedMailOutDto>(
      `/private/admin/email-customers/${userId}/sends/${recordType}/${recordId}/render`
    );
    return data;
  }

  static async adminResendEmail(
    userId: string,
    recordType: ETimelineRecordType,
    recordId: string,
    body: AdminActionInDto
  ) {
    await apiService.post(`/private/admin/email-customers/${userId}/sends/${recordType}/${recordId}/resend`, body);
  }

  // admin-emailbeheer blok B (opdracht §4), Scherm 1 - E-mailrapport.

  static async adminGetEmailReportSummary(from: string, to: string) {
    const { data } = await apiService.get<EmailReportSummaryOutDto>(`/private/admin/email-stats/summary`, {
      params: { from, to },
    });
    return data;
  }

  static async adminGetEmailReportSeries(from: string, to: string, granularity: EEmailReportGranularity) {
    const { data } = await apiService.get<EmailReportSeriesOutDto>(`/private/admin/email-stats/series`, {
      params: { from, to, granularity },
    });
    return data;
  }

  // admin-emailbeheer blok B (opdracht §6), Scherm 3 - Verzendlog.

  static async adminGetEmailLog(params: APIEmailLogQueryParams) {
    const { data } = await apiService.get<PageResponseEmailLogEntryOutDto>(`/private/admin/email-log`, { params });
    return data;
  }

  static async adminExportEmailLogCsv(params: Omit<APIEmailLogQueryParams, 'page' | 'size' | 'sort'>) {
    const { data } = await apiService.get<Blob>(`/private/admin/email-log/export`, {
      params,
      responseType: 'blob',
    });
    return data;
  }

  static async adminGetEmailLogContext(recordType: ETimelineRecordType, recordId: string) {
    // the backend returns a raw string, but axios auto-parses any JSON-shaped response body
    // regardless of Content-Type - `context` is always stored as JSON, so this is in practice
    // always parsed into an object before it reaches callers. Typed honestly so callers handle
    // both shapes instead of assuming string (see EmailLogTable's ContextDetailPanel).
    const { data } = await apiService.get<string | Record<string, unknown>>(
      `/private/admin/email-log/${recordType}/${recordId}/context`
    );
    return data;
  }

  // admin-emailbeheer blok B (opdracht §7), Scherm 4 - Bezorgproblemen.

  static async adminGetEmailDeliverability(from: string, to: string) {
    const { data } = await apiService.get<EmailDeliverabilityOutDto>(`/private/admin/email-deliverability`, {
      params: { from, to },
    });
    return data;
  }
}

export type APIEmailLogQueryParams = APIPaginationQueryParams & {
  from: string;
  to: string;
  mailKey?: string;
  category?: string;
  locale?: string;
  status?: string;
  recipientEmail?: string;
};
